// The emergency run in progress on this device (src/lib/contingency.js run). Kept in localStorage on every change, so a reload,
// a lost connection or leaving the page never loses a tick; the Checklists page (EmergencyRun.jsx), the Emergency button and the
// chat (AI "done" / "next") all go through here. It tells the assistant about the run (appContext.setEmergencyContext) only for
// the login that started it.
import { RUN_KEY, newRun, tick, skip, undo, nextOpen, stepNumber, speakStep, UNDO_MS } from './contingency.js'
import { setEmergencyContext } from './appContext.js'

const ls = () => { try { return typeof localStorage !== 'undefined' ? localStorage : null } catch (e) { return null } }
function load() { try { const s = ls() && ls().getItem(RUN_KEY); const v = s ? JSON.parse(s) : null; return v && v.run && v.run.steps ? v : null } catch (e) { return null } }
let state = load()          // { vesselId, uid, run, cur, view: 'one' | 'list', speakOn, aiUndo: { i, n, until, what } | null } | null
let me = null               // the login using the app now (setEmergencyUser)
const subs = new Set()
const ctxOf = s => (!s || !s.run || s.run.finishedAt || !me || s.uid !== me) ? null : {
  runId: s.run.id, title: s.run.title, mode: s.run.mode || 'emergency', startedAt: s.run.startedAt, startedBy: s.run.startedBy, uid: s.uid, tz: s.run.tz, current: s.cur,
  steps: s.run.steps.filter(x => x.check).map(x => { const m = s.run.marks[x.i]; return { i: x.i, n: stepNumber(s.run, x.i), title: x.title, status: m ? m.status : 'open', at: m ? m.at : null, by: m ? m.by : null } }),
}
function publish() {
  try { const st = ls(); if (st) { if (state) st.setItem(RUN_KEY, JSON.stringify(state)); else st.removeItem(RUN_KEY) } } catch (e) {}
  try { setEmergencyContext(ctxOf(state)) } catch (e) {}
  subs.forEach(f => { try { f(state) } catch (e) {} })
}
export const getEmergency = () => state
export function subscribeEmergency(fn) { subs.add(fn); return () => subs.delete(fn) }
// the login (and vessel) using the app: a run started by another login / on another vessel is not offered to the assistant
export function setEmergencyUser(uid, vesselId) { me = uid || null; if (state && vesselId && state.vesselId && state.vesselId !== vesselId) { /* another vessel's run stays stored, not shown */ } publish() }
export const runFor = vesselId => (state && state.run && !state.run.finishedAt && (!vesselId || state.vesselId === vesselId) ? state : null)

const uuid = () => { try { return crypto.randomUUID() } catch (e) { return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)) } }
// mode 'drill' + drillType: a drill of the plan (src/lib/drillPlans.js) - attendance is kept here too. One run per device: a new
// run replaces the open one (the pages ask first, naming it)
export function startEmergency({ checklist, who, uid, vesselId, tz, at, mode, drillType }) {
  const run = newRun({ checklist, who, at, tz, id: uuid(), mode, drillType })
  state = { vesselId: vesselId || null, uid: uid || null, run, cur: nextOpen(run), view: (state && state.view) || 'one', speakOn: !!(state && state.speakOn), aiUndo: null, attendance: {} }
  publish()
  return state
}
const set = patch => { if (!state) return null; state = { ...state, ...patch }; publish(); return state }
export const setCursor = i => set({ cur: i })
export const setView = view => set({ view })
export const setSpeakOn = speakOn => set({ speakOn })
// drill attendance: { name: true (present) | false } (kept with the run, so a reload keeps who was ticked present)
export const setPresent = (name, on) => (state ? set({ attendance: { ...(state.attendance || {}), [name]: !!on } }) : null)
export const setAllPresent = (names, on) => (state ? set({ attendance: { ...(state.attendance || {}), ...Object.fromEntries(names.map(n => [n, !!on])) } }) : null)
export function clearEmergency() { state = null; publish() }
export function markFinished(at) { if (!state) return null; return set({ run: { ...state.run, finishedAt: new Date(at || Date.now()).toISOString() } }) }

// the run's own actions (the buttons): after a tick / skip the cursor goes to the next open step
export function doTick(i, who) { if (!state) return null; const run = tick(state.run, i, who); return set({ run, cur: nextOpen(run, i + 1) }) }
export function doSkip(i, reason, who) { if (!state) return null; const run = skip(state.run, i, reason, who); return set({ run, cur: nextOpen(run, i + 1) }) }
export function doUndo(i, who) { if (!state) return null; const run = undo(state.run, i, who); return set({ run, cur: i, aiUndo: state.aiUndo && state.aiUndo.i === i ? null : state.aiUndo }) }

// read a step aloud with the device's own voice (no AI, works offline)
export function speak(text) {
  try {
    const s = typeof window !== 'undefined' && window.speechSynthesis
    if (!s || !text) return false
    s.cancel(); const u = new SpeechSynthesisUtterance(String(text)); u.lang = 'en-GB'; u.rate = 0.95; s.speak(u); return true
  } catch (e) { return false }
}
export const speakCurrent = () => (state ? speak(speakStep(state.run, state.cur)) : false)

// an action the assistant returned (api/_contingency.js emergency_step) -> applied here, only to the same run of the same login.
// A tick / skip by the assistant can be undone for 30 s (aiUndo). -> { ok, text, undo: { i, n, until } | null }
export function applyAiAction(a, who, uid, now = Date.now()) {
  if (!a || !state || !state.run || state.run.finishedAt) return { ok: false, text: 'No emergency run is open on this device - nothing was ticked.' }
  if (a.runId !== state.run.id || (uid && state.uid !== uid)) return { ok: false, text: 'That step belongs to another run - nothing was ticked.' }
  const st = state.run.steps[a.i]
  try {
    if (a.action === 'goto' || a.action === 'speak') {
      if (a.action === 'goto' && st && st.check) set({ cur: a.i })
      const text = a.action === 'goto' ? speakStep(state.run, a.i) : a.text
      if (a.speak !== false) speak(text)
      return { ok: true, text, undo: null }
    }
    if (a.action === 'undo') { doUndo(a.i, who); return { ok: true, text: 'Step ' + a.n + ' is open again.', undo: null } }
    if (a.action === 'tick') doTick(a.i, who)
    else if (a.action === 'skip') doSkip(a.i, a.reason, who)
    else return { ok: false, text: 'Unknown action' }
    const u = { i: a.i, n: a.n, until: now + (a.undoMs || UNDO_MS), what: a.action === 'skip' ? 'skipped' : 'ticked' }
    set({ aiUndo: u })
    if (a.speakNext && state.speakOn) speakCurrent()
    return { ok: true, text: 'Step ' + a.n + ' ' + u.what + '.', undo: u }
  } catch (e) { return { ok: false, text: e.message } }
}
// the assistant's tick undone within its 30 s
export function undoAi(i, who, now = Date.now()) {
  if (!state || !state.aiUndo || state.aiUndo.i !== i || now > state.aiUndo.until) return false
  doUndo(i, who); return true
}
